import { Box, Button, Input, Text } from '@chakra-ui/react';
import { useLoadingState } from '../../hooks/useLoadingState';
import { LoadingPanel } from '../brand/TwindenLoader';

// The pieces every paged admin list shares (#73): the count above it, the
// search box, and "Load more" below with the M66 inline loader (D250).

/** What these pieces read from a `usePagedList` result. */
interface ListView {
  items: unknown[] | null;
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  moreError: unknown;
  loadMore: () => Promise<void>;
}

/** "120 families", or "Showing 50 of 120 families" while more remain. */
export function adminCountLabel(
  total: number,
  shown: number,
  one: string,
  many: string
): string {
  const noun = total === 1 ? one : many;
  return shown < total
    ? `Showing ${shown} of ${total} ${noun}`
    : `${total} ${noun}`;
}

export function ListCount({
  list,
  one,
  many,
}: {
  list: ListView;
  one: string;
  many: string;
}) {
  if (!list.items) return null;
  return (
    <Text fontSize="sm" color="muted" data-testid="admin-count">
      {adminCountLabel(list.total, list.items.length, one, many)}
    </Text>
  );
}

export function AdminSearch({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <Input
      size="sm"
      maxW={{ base: 'full', md: '360px' }}
      type="search"
      placeholder={placeholder}
      aria-label={label}
      value={value}
      maxLength={100}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** "Load more", or the inline mark while the next page loads. */
export function LoadMore({ list }: { list: ListView }) {
  const state = useLoadingState({
    loading: list.loadingMore,
    error: list.moreError,
    retry: () => void list.loadMore(),
  });
  if (!list.items || !list.hasMore) return null;
  if (state.show) return <LoadingPanel state={state} size="inline" />;
  return (
    <Box textAlign="center" py={2}>
      <Button
        size="sm"
        variant="outline"
        colorScheme="gray"
        borderColor="line"
        isDisabled={list.loadingMore}
        onClick={() => void list.loadMore()}
      >
        Load more
      </Button>
    </Box>
  );
}
